import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidV4 } from 'uuid';

import roundToPrecision from '../../utils/roundToPrecision';
import EDIT_TOOL_TYPES from '../../data/edit-tool-types';
import SEGMENTATION_LUNG_TYPES from '../../data/segmentation-lung-types';

import { SegmentationCanvasStyle } from './SegmentationCanvasStyle';
import {
  actionShape,
  idTypes,
  segmentationShape,
} from '../../utils/propTypesShapes';

const EXAM_IMAGE_SIZE = 512;
const SLICE_GAP = 2 * 5;

const SegmentationCanvas = ({
  imageIndex,
  initalZ,
  radius,
  eraser,
  classificated,
  mask,
  original,
  tool,
  type,
  orignalRef,
  actionsList,
  classificateds,
  masks,
  originals,
  segmentations,
  undoActionsList,
  updateActionsList,
  updateGuidePosition,
  updateSegmentationList,
  updateShowGuide,
}) => {
  const segmentationActionRef = useRef();

  const [activeDraw, setActiveDraw] = useState(false);
  const [z, setZ] = useState(initalZ || 0);

  const impactedSlicesAmount = useMemo(
    () => Math.ceil(radius / SLICE_GAP),
    [radius]
  );

  const undoPointsId = useMemo(() => {
    const current = [];

    if (undoActionsList?.length) {
      undoActionsList?.forEach((action) => {
        action?.points?.forEach((point) => current.push(point.id));
      });
    }

    return current;
  }, [undoActionsList]);

  const impactedSlices = useMemo(() => {
    const current = [];

    for (let i = 1; i < impactedSlicesAmount; i += 1) {
      const index = imageIndex - i - 1;

      if (originals[index]?.length) {
        current.unshift({
          original: originals[index],
          classificated: classificateds[index],
          mask: masks[index],
          radius: Math.sqrt(radius * radius - SLICE_GAP * i * SLICE_GAP * i),
        });
      }
    }

    current.push({ original, classificated, mask, radius });

    for (let i = 1; i < impactedSlicesAmount; i += 1) {
      const index = imageIndex + i - 1;

      if (originals[index]?.length) {
        current.push({
          original: originals[index],
          classificated: classificateds[index],
          mask: masks[index],
          radius: Math.sqrt(radius * radius - SLICE_GAP * i * SLICE_GAP * i),
        });
      }
    }

    return current;
  }, [
    classificated,
    classificateds,
    imageIndex,
    impactedSlicesAmount,
    mask,
    masks,
    original,
    originals,
    radius,
  ]);

  const drawSegmentation = (x, y) => {
    const currentSegmentations = segmentations || [];
    const currentIndex = currentSegmentations?.length - 1;
    const lastSegmentation = currentSegmentations[currentIndex];
    let currentZ = z;

    if (eraser && lastSegmentation?.type !== 'eraser') {
      currentZ += 1;

      setZ(currentZ);
    }

    const currentPoint = {
      id: uuidV4(),
      x,
      y,
      z: currentZ,
      type: eraser ? 'eraser' : type,
      original,
      classificated,
      mask,
      impactedSlices,
    };
    const currentActionsList = actionsList;

    if (!currentActionsList?.length) {
      currentActionsList.push({
        id: uuidV4(),
        type: EDIT_TOOL_TYPES?.segmentation?.key,
        points: [],
      });
    }

    const lastAction = currentActionsList[currentActionsList?.length - 1];

    if (lastAction) {
      lastAction.points = [...lastAction?.points, currentPoint];
      currentActionsList?.splice(currentActionsList?.length - 1, 1, lastAction);
    }

    updateActionsList([...currentActionsList]);
    updateSegmentationList([...currentSegmentations, currentPoint]);
  };

  const onMouseMoveSegmentation = (event) => {
    if (orignalRef?.current && segmentationActionRef?.current) {
      const { clientHeight } = orignalRef?.current;
      const clientRect =
        segmentationActionRef?.current?.getBoundingClientRect();
      const fixY = roundToPrecision((clientHeight - EXAM_IMAGE_SIZE) / 2, 0);
      let x = 0;
      let y = 0;

      x += event?.clientX || 0;
      x -= clientRect?.left || 0;
      x = x > 0 ? x : 0;
      y += event?.clientY || 0;
      y -= clientRect?.top || 0;
      y = y > 0 ? y : 0;

      if (activeDraw) {
        drawSegmentation(x, y);
      }

      y += fixY || 0;
      y = y > 0 ? y : 0;

      updateGuidePosition({ x, y });
    }
  };

  const onClickSegmentation = (event) => {
    if (orignalRef?.current && segmentationActionRef?.current) {
      updateActionsList([
        ...actionsList,
        {
          id: uuidV4(),
          type: EDIT_TOOL_TYPES?.segmentation?.key,
          points: [],
        },
      ]);

      const clientRect =
        segmentationActionRef?.current?.getBoundingClientRect();
      let x = 0;
      let y = 0;

      x += event?.clientX || 0;
      x -= clientRect?.left || 0;
      x = x > 0 ? x : 0;

      y += event?.clientY || 0;
      y -= clientRect?.top || 0;
      y = y > 0 ? y : 0;

      drawSegmentation(x, y);
    }
  };

  const onMouseUpSegmentation = () => {
    updateActionsList([
      ...actionsList,
      {
        id: uuidV4(),
        type: EDIT_TOOL_TYPES?.segmentation?.key,
        points: [],
      },
    ]);
    setActiveDraw(false);
  };

  const renderSegmentationPonits = useMemo(() => {
    const current = [];

    for (let i = 0; i <= z; i += 1) {
      const maskId = uuidV4();
      const eraserPoints = segmentations.filter(
        (segmentation) =>
          segmentation?.type === 'eraser' &&
          segmentation?.z > i &&
          !undoPointsId.includes(segmentation?.id)
      );
      const brushPoints = segmentations.filter(
        (segmentation) =>
          Object.keys(SEGMENTATION_LUNG_TYPES).includes(segmentation?.type) &&
          segmentation?.z === i &&
          !undoPointsId.includes(segmentation?.id)
      );

      if (eraserPoints?.length || brushPoints?.length) {
        current.push(
          <svg
            key={maskId}
            className="segmentation-list-container"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
          >
            {eraserPoints?.length ? (
              <defs>
                <mask id={maskId}>
                  <rect width="100%" height="100%" fill="white" />
                  {eraserPoints?.map((point) => {
                    if (point && !undoPointsId.includes(point?.id)) {
                      const r = point?.impactedSlices.find(
                        (iS) => iS?.original === original
                      )?.radius;

                      return (
                        <circle
                          key={point?.id}
                          fill="black"
                          className={`type-${point?.type}`}
                          cx={point?.x}
                          cy={point?.y}
                          r={r}
                        />
                      );
                    }

                    return null;
                  })}
                </mask>
              </defs>
            ) : null}
            {brushPoints.length ? (
              <g mask={`url(#${maskId})`}>
                {brushPoints?.map((point) => {
                  if (point && !undoPointsId.includes(point?.id)) {
                    const r = point?.impactedSlices.find(
                      (iS) => iS?.original === original
                    )?.radius;

                    return (
                      <circle
                        key={point?.id}
                        className={`type-${point?.type}`}
                        cx={point?.x}
                        cy={point?.y}
                        r={r}
                      />
                    );
                  }

                  return null;
                })}
              </g>
            ) : null}
          </svg>
        );
      }
    }

    if (current?.length) {
      return current;
    }

    return null;
  }, [z, segmentations, undoPointsId, original]);

  return (
    <SegmentationCanvasStyle>
      {tool === EDIT_TOOL_TYPES?.segmentation?.key
        ? renderSegmentationPonits
        : null}
      {tool === EDIT_TOOL_TYPES?.segmentation?.key ? (
        <button
          ref={segmentationActionRef}
          className="segmentation-button"
          type="button"
          disabled={tool !== EDIT_TOOL_TYPES?.segmentation?.key}
          aria-label="Segmentation actions"
          onMouseEnter={() => updateShowGuide(true)}
          onMouseLeave={() => updateShowGuide(false)}
          onMouseDown={() => setActiveDraw(true)}
          onMouseUp={onMouseUpSegmentation}
          onMouseMove={onMouseMoveSegmentation}
          onClick={onClickSegmentation}
        />
      ) : null}
    </SegmentationCanvasStyle>
  );
};

SegmentationCanvas.propTypes = {
  imageIndex: PropTypes.number,
  initalZ: PropTypes.number,
  radius: PropTypes.number,
  eraser: PropTypes.bool,
  classificated: idTypes,
  mask: idTypes,
  original: idTypes,
  tool: PropTypes.oneOf(['', ...Object.keys(EDIT_TOOL_TYPES)]),
  type: PropTypes.oneOf(['', ...Object.keys(SEGMENTATION_LUNG_TYPES)]),
  orignalRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  actionsList: PropTypes.arrayOf(actionShape),
  classificateds: PropTypes.arrayOf(PropTypes.string),
  masks: PropTypes.arrayOf(idTypes),
  originals: PropTypes.arrayOf(PropTypes.string),
  segmentations: PropTypes.arrayOf(segmentationShape),
  undoActionsList: PropTypes.arrayOf(actionShape),
  updateActionsList: PropTypes.func.isRequired,
  updateGuidePosition: PropTypes.func.isRequired,
  updateSegmentationList: PropTypes.func.isRequired,
  updateShowGuide: PropTypes.func.isRequired,
};

SegmentationCanvas.defaultProps = {
  imageIndex: 0,
  initalZ: 0,
  radius: 0,
  eraser: false,
  classificated: '',
  mask: '',
  original: '',
  tool: '',
  type: '',
  actionsList: [],
  classificateds: [],
  masks: [],
  originals: [],
  segmentations: [],
  undoActionsList: [],
};

export default SegmentationCanvas;
